import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { useAuthorizationStore } from '@/stores/authorization';
import { useSystemDocumentsStore } from '@/stores/systemDocuments';
import {
  acceptSystemDocuments,
  fetchCurrentSystemDocuments,
  type SystemDocumentKey,
} from '@/services/systemDocumentService';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';

export const LEGAL_DOCUMENT_ROUTES: Record<SystemDocumentKey, string> = {
  terms_of_use: '/legal/terms-of-use',
  privacy_policy: '/legal/privacy-policy',
};

// public reading pages must stay visible while the acceptance is pending
export const isLegalDocumentRoute = (pathname: string): boolean =>
  Object.values(LEGAL_DOCUMENT_ROUTES).includes(pathname);

const TERMS_BULLET_KEYS = [1, 2, 3, 4, 5, 6].map(
  (i) => `system_documents.terms_bullet_${i}`,
);
const PRIVACY_BULLET_KEYS = [1, 2, 3, 4, 5, 6].map(
  (i) => `system_documents.privacy_bullet_${i}`,
);

export const SystemDocumentAcceptanceModal = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { isAuthenticated, user, contextSelectionRequired, logout } =
    useAuthStore();
  const selectedModuleKey = useAuthorizationStore((s) => s.selectedModuleKey);
  const acceptanceRequiredByApi = useSystemDocumentsStore(
    (s) => s.acceptanceRequiredByApi,
  );
  const setAcceptanceRequiredByApi = useSystemDocumentsStore(
    (s) => s.setAcceptanceRequiredByApi,
  );

  const [termsChecked, setTermsChecked] = useState(false);
  const [privacyChecked, setPrivacyChecked] = useState(false);
  const [confirmingCancel, setConfirmingCancel] = useState(false);

  const isEligible =
    !isLegalDocumentRoute(location.pathname) &&
    isAuthenticated &&
    user?.auth_scope === 'web' &&
    !user?.professional_id &&
    selectedModuleKey !== null &&
    !contextSelectionRequired;

  const { data } = useQuery({
    queryKey: ['system-documents-current'],
    queryFn: fetchCurrentSystemDocuments,
    enabled: isEligible,
    staleTime: 1000 * 60 * 5,
  });

  const acceptMutation = useMutation({
    mutationFn: acceptSystemDocuments,
    onSuccess: async () => {
      setAcceptanceRequiredByApi(false);
      await queryClient.invalidateQueries({ refetchType: 'active' });
      toast({
        title:
          t('system_documents.accept_success') ||
          'Aceite registrado com sucesso',
        variant: 'success',
      });
    },
    onError: () => {
      toast({
        title:
          t('system_documents.accept_error') ||
          'Não foi possível registrar o aceite. Tente novamente.',
        variant: 'destructive',
      });
    },
  });

  const requiresAcceptance =
    isEligible && (data?.requires_acceptance || acceptanceRequiredByApi);

  if (!requiresAcceptance) return null;

  const openDocument = (key: SystemDocumentKey) => {
    window.open(LEGAL_DOCUMENT_ROUTES[key], '_blank', 'noopener,noreferrer');
  };

  const handleAccept = () => {
    if (!termsChecked || !privacyChecked || acceptMutation.isPending) return;
    acceptMutation.mutate();
  };

  const handleConfirmCancel = () => {
    setConfirmingCancel(false);
    logout();
  };

  if (confirmingCancel) {
    return (
      <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 p-4 sm:p-6">
        <div className="w-full max-w-lg rounded-2xl bg-[#F9FCFF] p-8 shadow-2xl sm:p-10">
          <h2 className="text-xl font-bold text-[#2B4BC0] sm:text-2xl">
            {t('system_documents.cancel_title') || 'Cancelar aceite?'}
          </h2>
          <p className="mt-4 text-[15px] leading-7 text-[#3F4B5F]">
            {t('system_documents.cancel_description') ||
              'O sistema não pode ser acessado sem o aceite dos documentos. Ao confirmar, você será desconectado.'}
          </p>
          <div className="mt-8 flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="outline"
              className="h-11 min-w-[140px] rounded-lg border-[#2563EB] text-[15px] font-semibold text-[#2563EB] hover:bg-[#EFF6FF] hover:text-[#1D4ED8]"
              onClick={() => setConfirmingCancel(false)}
            >
              {t('system_documents.cancel_back') || 'Voltar'}
            </Button>
            <Button
              type="button"
              className="h-11 min-w-[160px] rounded-lg bg-[#1E3A6E] text-[15px] font-semibold text-white hover:bg-[#182F5A]"
              onClick={handleConfirmCancel}
            >
              {t('system_documents.cancel_confirm') || 'Sair do sistema'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/70 p-4 sm:p-6">
      <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col rounded-2xl bg-[#F9FCFF] shadow-2xl">
        <button
          type="button"
          onClick={() => setConfirmingCancel(true)}
          disabled={acceptMutation.isPending}
          className="absolute right-5 top-5 z-10 rounded-md p-1 text-[#8A94A6] transition hover:text-[#4B5563]"
          aria-label={t('common.close') || 'Fechar'}
        >
          <X className="h-6 w-6" strokeWidth={2.5} />
        </button>

        <div className="overflow-y-auto px-8 pb-4 pt-16 sm:px-10">
          <h2 className="text-2xl font-bold text-[#2B4BC0] sm:text-[1.75rem]">
            {t('system_documents.modal_title') ||
              'Termos de Uso e Política de Privacidade'}
          </h2>

          <p className="mt-5 text-[15px] leading-7 text-[#3F4B5F]">
            {t('system_documents.modal_description') ||
              'Para garantir uma utilização segura da plataforma e proteger seus dados pessoais, pedimos que leia atentamente os documentos abaixo antes de continuar.'}
          </p>

          <section className="mt-7">
            <h3 className="text-lg font-semibold text-[#2A3648]">
              {t('system_documents.terms_of_use') || 'Termo de Uso'}
            </h3>
            <p className="mt-2 text-[15px] leading-7 text-[#3F4B5F]">
              {t('system_documents.terms_description') ||
                'Este documento estabelece as condições para utilização da plataforma Geoescala, incluindo:'}
            </p>
            <ul className="mt-4 list-disc space-y-1.5 pl-6 text-[15px] leading-6 text-[#3F4B5F] marker:text-[#8A94A6]">
              {TERMS_BULLET_KEYS.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ul>
            <Button
              type="button"
              variant="ghost"
              className="mt-5 h-9 rounded-lg bg-[#E3EFFC] px-8 text-sm font-semibold text-[#2563EB] hover:bg-[#D3E5FA] hover:text-[#1D4ED8]"
              onClick={() => openDocument('terms_of_use')}
            >
              {t('system_documents.read_terms') || 'Ler Termo de Uso'}
            </Button>
          </section>

          <section className="mt-8">
            <h3 className="text-lg font-semibold text-[#2A3648]">
              {t('system_documents.privacy_policy') ||
                'Política de Privacidade'}
            </h3>
            <p className="mt-2 text-[15px] leading-7 text-[#3F4B5F]">
              {t('system_documents.privacy_description') ||
                'Este documento explica como a Geoescala coleta, utiliza, armazena e protege seus dados pessoais, em conformidade com a Lei Geral de Proteção de Dados (LGPD).'}
            </p>
            <ul className="mt-4 list-disc space-y-1.5 pl-6 text-[15px] leading-6 text-[#3F4B5F] marker:text-[#8A94A6]">
              {PRIVACY_BULLET_KEYS.map((key) => (
                <li key={key}>{t(key)}</li>
              ))}
            </ul>
            <Button
              type="button"
              variant="ghost"
              className="mt-5 h-9 rounded-lg bg-[#E3EFFC] px-8 text-sm font-semibold text-[#2563EB] hover:bg-[#D3E5FA] hover:text-[#1D4ED8]"
              onClick={() => openDocument('privacy_policy')}
            >
              {t('system_documents.read_privacy') ||
                'Ler Política de Privacidade'}
            </Button>
          </section>

          <div className="mt-9 space-y-5 pb-2">
            <label className="flex cursor-pointer items-center gap-4 text-[15px] text-[#3F4B5F]">
              <Checkbox
                checked={termsChecked}
                onCheckedChange={(checked) => setTermsChecked(Boolean(checked))}
                disabled={acceptMutation.isPending}
                className="h-5 w-5 rounded-[5px] border-[#2563EB] data-[state=checked]:border-[#2563EB] data-[state=checked]:bg-[#2563EB]"
              />
              <span>
                {t('system_documents.terms_checkbox') ||
                  'Li e concordo com o Termo de Uso.'}
              </span>
            </label>

            <label className="flex cursor-pointer items-center gap-4 text-[15px] text-[#3F4B5F]">
              <Checkbox
                checked={privacyChecked}
                onCheckedChange={(checked) =>
                  setPrivacyChecked(Boolean(checked))
                }
                disabled={acceptMutation.isPending}
                className="h-5 w-5 rounded-[5px] border-[#2563EB] data-[state=checked]:border-[#2563EB] data-[state=checked]:bg-[#2563EB]"
              />
              <span>
                {t('system_documents.privacy_checkbox') ||
                  'Li e concordo com a Política de Privacidade.'}
              </span>
            </label>
          </div>
        </div>

        <div className="border-t border-[#E4EAF2] px-8 py-5 sm:px-10">
          <div className="flex items-center justify-between gap-3">
            <Button
              type="button"
              variant="outline"
              className="h-11 min-w-[180px] rounded-lg border-[#2563EB] text-[15px] font-semibold text-[#2563EB] hover:bg-[#EFF6FF] hover:text-[#1D4ED8]"
              onClick={() => setConfirmingCancel(true)}
              disabled={acceptMutation.isPending}
            >
              {t('common.cancel') || 'Cancelar'}
            </Button>
            <Button
              type="button"
              className="h-11 min-w-[180px] rounded-lg bg-[#1E3A6E] text-[15px] font-semibold text-white hover:bg-[#182F5A]"
              onClick={handleAccept}
              disabled={
                !termsChecked || !privacyChecked || acceptMutation.isPending
              }
            >
              {acceptMutation.isPending
                ? t('system_documents.accepting') || 'Registrando...'
                : t('system_documents.accept_button') || 'Aceitar e continuar'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
